/**
 * Pipeline funnel — horizontal stacked bar showing distribution of keywords
 * across the 7 lifecycle stages. Hovering shows count + percentage. Clicking
 * filters the pipeline (caller-provided).
 */

import type { Status } from "../../lib/api.js";

export interface FunnelStage {
  status: Status;
  count: number;
}

const STAGE_ORDER: Status[] = [
  "backlog",
  "researching",
  "briefed",
  "drafting",
  "reviewing",
  "published",
  "distributed",
];

const STAGE_LABEL: Record<Status, string> = {
  backlog: "Backlog",
  researching: "Researching",
  briefed: "Briefed",
  drafting: "Drafting",
  reviewing: "Reviewing",
  published: "Published",
  distributed: "Distributed",
  archived: "Archived",
  rejected: "Rejected",
};

const STAGE_COLOR: Record<Status, string> = {
  backlog: "bg-slate-300",
  researching: "bg-amber-400",
  briefed: "bg-blue-400",
  drafting: "bg-indigo-400",
  reviewing: "bg-violet-400",
  published: "bg-brand-500",
  distributed: "bg-emerald-500",
  archived: "bg-slate-200",
  rejected: "bg-rose-300",
};

export function FunnelCard({
  stages,
  total,
  onSelect,
}: {
  stages: Record<Status, number>;
  total: number;
  onSelect?: (status: Status) => void;
}) {
  const orderedStages: FunnelStage[] = STAGE_ORDER.map((s) => ({ status: s, count: stages[s] ?? 0 }));
  const sum = orderedStages.reduce((acc, s) => acc + s.count, 0) || 1;

  return (
    <div className="card p-5">
      <div className="section-heading mb-4">
        <div>
          <h3 className="text-sm font-semibold text-ink">Pipeline funnel</h3>
          <p className="mt-0.5 text-xs text-muted">{total} keywords across all lifecycle stages</p>
        </div>
      </div>

      {/* Stacked bar */}
      <div className="flex h-3 overflow-hidden rounded-full bg-line-soft">
        {orderedStages.map((s) => {
          if (s.count === 0) return null;
          const pct = (s.count / sum) * 100;
          return (
            <button
              key={s.status}
              onClick={onSelect ? () => onSelect(s.status) : undefined}
              className={`${STAGE_COLOR[s.status]} transition hover:brightness-110`}
              style={{ width: `${pct}%` }}
              title={`${STAGE_LABEL[s.status]} — ${s.count} (${pct.toFixed(0)}%)`}
            />
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3 lg:grid-cols-7">
        {orderedStages.map((s) => (
          <button
            key={s.status}
            onClick={onSelect ? () => onSelect(s.status) : undefined}
            className="group flex items-center gap-2 text-left"
            disabled={!onSelect}
          >
            <span className={`h-2 w-2 shrink-0 rounded-full ${STAGE_COLOR[s.status]}`} />
            <span className="min-w-0">
              <div className="truncate text-xs font-medium text-ink group-hover:text-brand-700">{STAGE_LABEL[s.status]}</div>
              <div className="text-2xs text-muted">{s.count} · {Math.round((s.count / sum) * 100)}%</div>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
